import {
  NameBasedMaterialLimit,
  NameBasedTargetInventoryItem,
} from "store/api/generatedApi";

export interface RulePeriodSelector {
  is_deployable: boolean | null;
  position: "all" | "first" | "last";
}
export const inventoryPeriodGroups = [
  {
    group: "first_deployable",
    label: "autoFirstDeployablePeriod",
    isDeployable: true,
    position: "first",
  },
  {
    group: "other_deployable",
    label: "autoOtherDeployablePeriods",
    isDeployable: true,
    position: "other",
  },
  {
    group: "last_deployable",
    label: "autoLastDeployablePeriod",
    isDeployable: true,
    position: "last",
  },
  {
    group: "first_forecast",
    label: "autoFirstForecastPeriod",
    isDeployable: false,
    position: "first",
  },
  {
    group: "other_forecast",
    label: "autoOtherForecastPeriods",
    isDeployable: false,
    position: "other",
  },
  {
    group: "last_forecast",
    label: "autoLastForecastPeriod",
    isDeployable: false,
    position: "last",
  },
] as const;
export type InventoryPeriodGroup =
  (typeof inventoryPeriodGroups)[number]["group"];
export type InventoryTargetBound = "min_quantity" | "max_quantity";
export type InventoryTargetCopyScope = "all" | "deployable" | "forecast";
export interface PeriodInventoryTargets {
  periods:
    | RulePeriodSelector
    | { group: InventoryPeriodGroup; bound: InventoryTargetBound };
  targets: NameBasedTargetInventoryItem[];
}
export interface PeriodMaterialExclusivity {
  periods: RulePeriodSelector;
  groups: string[][];
}
export type InventoryTargetsByPeriod = Record<
  string,
  NameBasedTargetInventoryItem[]
>;
export type MaterialInventoryTargetsByPeriod = Record<
  string,
  NameBasedTargetInventoryItem
>;

export interface ConditionalInventoryTargets {
  rule_index: number;
  condition: string;
  targets_by_period: InventoryTargetsByPeriod;
}
export interface ConditionalMaterialLimits {
  rule_index: number;
  condition: string;
  limits: NameBasedMaterialLimit[];
}
export const allPeriods: RulePeriodSelector = {
  is_deployable: null,
  position: "all",
};
export const selectedPeriodIndices = ({
  selector,
  deployable,
  forecast,
}: {
  selector: RulePeriodSelector | { group: InventoryPeriodGroup };
  deployable: number;
  forecast: number;
}): number[] => {
  if ("group" in selector) {
    const definition = inventoryPeriodGroups.find(
      ({ group }) => group === selector.group
    );
    if (!definition) return [];
    const periods = selectedPeriodIndices({
      selector: { is_deployable: definition.isDeployable, position: "all" },
      deployable,
      forecast,
    });
    switch (definition.position) {
      case "first":
        return periods.slice(0, 1);
      case "other":
        return periods.slice(1, -1);
      case "last":
        return periods.length > 1 ? periods.slice(-1) : [];
    }
  }
  const periods = Array.from(
    { length: deployable + forecast },
    (_, index) => index + 1
  ).filter(
    (period) =>
      selector.is_deployable === null ||
      period <= deployable === selector.is_deployable
  );
  switch (selector.position) {
    case "first":
      return periods.slice(0, 1);
    case "last":
      return periods.slice(-1);
    case "all":
      return periods;
  }
};
